/**
 * CampaignsPanel (MKT-REDESIGN-01 M1, 2026-09-03) — every send is a record.
 * The list answers "what did we send, to whom, and how did it go"; a row
 * expands to its recipients. Cancel stops a scheduled or in-flight campaign
 * cold; Release is the deliberate go-live step for a campaign that was held
 * while comms were in testing.
 */
import { useState } from "react";
import { Send, XCircle, PlayCircle, ChevronDown, ChevronRight, Users, Clock, CheckCircle2, AlertTriangle, List, CalendarDays, MessageSquare, Mail, Plus } from "lucide-react";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { displayCustomerName } from "@/lib/format";
import { useCommsLive } from "@/features/comms/comms";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useCampaigns, useCampaignRecipients, useCampaignResults, useCancelCampaign, useReleaseCampaign, useLaunchCampaign, estimateDuration, STATUS_META, type Campaign, type CampaignResults } from "./campaigns";
import { MarketingCalendar } from "./MarketingCalendar";

const when = (iso: string | null) => iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

export function CampaignsPanel({ templateNames, onNewText }: { templateNames: Record<string, string>; onNewText?: () => void }) {
  const { data, isLoading } = useCampaigns();
  const live = useCommsLive().data ?? false;
  const cancel = useCancelCampaign();
  // MKT decision "Who can send": scheduling / releasing / cancelling needs
  // marketing.send (the RPCs enforce it too — this just keeps the buttons honest).
  const canSend = useCan("marketing.send");
  const release = useReleaseCampaign();
  const [open, setOpen] = useState<string | null>(null);
  // M5: list by default; the calendar shows the same campaigns on the day they
  // run, next to the automations' traffic and the month's seasonal theme.
  const [view, setView] = useState<"list" | "calendar">("list");
  const rows = data ?? [];

  return (
    <section className="mb-8">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900">
          <Send className="h-4 w-4 text-brand-600" /> Campaigns
          <InfoTip label="How campaigns send">Emails and texts go out 100 every 10 minutes, and a campaign can be cancelled any time.</InfoTip>
        </h2>
        <div className="inline-flex rounded-lg border border-hairline bg-white p-0.5 text-xs" role="tablist" aria-label="Campaign view">
          <button type="button" role="tab" aria-selected={view === "list"} onClick={() => setView("list")}
            className={cn("inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium", view === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800")}><List className="h-3.5 w-3.5" /> List</button>
          <button type="button" role="tab" aria-selected={view === "calendar"} onClick={() => setView("calendar")}
            className={cn("inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium", view === "calendar" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800")}><CalendarDays className="h-3.5 w-3.5" /> Calendar</button>
        </div>
        <span className="text-xs text-slate-500">{rows.length ? `${rows.length} on record` : "nothing sent yet"}</span>
        <span className="ml-auto flex items-center gap-2">
          {!live && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-200"><AlertTriangle className="h-3 w-3" /> Comms in testing — new campaigns are held, nothing reaches a customer</span>}
          {onNewText && canSend && (
            <button type="button" onClick={onNewText} className="inline-flex h-8 items-center gap-1 rounded-lg border border-hairline bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50" title="A short text to customers who opted in to marketing texts">
              <Plus className="h-3.5 w-3.5" /> Text campaign
            </button>
          )}
        </span>
      </div>

      {view === "calendar" ? (
        <MarketingCalendar />
      ) : isLoading ? (
        <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-hairline bg-white px-4 py-6 text-center text-sm text-slate-500">
          No campaigns yet. Open a template below and choose <b>Send as a campaign</b>, start a <b>Text campaign</b> above, or build any list on Reports → All Customers and choose <b>Email these customers</b>.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-hairline bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline bg-slate-50/70 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Campaign</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Audience</th>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2 text-right">Progress</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((c) => {
                const meta = STATUS_META[c.status];
                const total = c.recipient_count;
                const done = c.sent_count + c.failed_count + c.suppressed_count;
                const isOpen = open === c.id;
                const canCancel = canSend && ["scheduled", "held", "sending"].includes(c.status);
                return (
                  <CampaignRow key={c.id} c={c} meta={meta} total={total} done={done} isOpen={isOpen}
                    templateName={c.template_key ? (templateNames[c.template_key] ?? c.template_key) : "—"}
                    onToggle={() => setOpen(isOpen ? null : c.id)}
                    onCancel={canCancel ? () => { if (window.confirm(`Cancel "${c.name}"? Anything not yet sent stays unsent.`)) cancel.mutate(c.id); } : undefined}
                    onRelease={canSend && c.status === "held" && live ? () => { if (window.confirm(`Release "${c.name}"? ${c.recipient_count.toLocaleString()} emails will start going out within 10 minutes.`)) release.mutate(c.id); } : undefined}
                    busy={cancel.isPending || release.isPending} />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {(cancel.isError || release.isError) && (
        <p className="mt-2 text-xs text-red-700">{String(((cancel.error ?? release.error) as Error)?.message ?? "Could not update the campaign.")}</p>
      )}
    </section>
  );
}

function CampaignRow({ c, meta, total, done, isOpen, templateName, onToggle, onCancel, onRelease, busy }: {
  c: Campaign; meta: { label: string; chip: string }; total: number; done: number; isOpen: boolean; templateName: string;
  onToggle: () => void; onCancel?: () => void; onRelease?: () => void; busy: boolean;
}) {
  const recipients = useCampaignRecipients(isOpen ? c.id : null);
  const results = useCampaignResults(isOpen && c.sent_count > 0 ? c.id : null);
  return (
    <>
      <tr className="align-top hover:bg-slate-50/60">
        <td className="px-3 py-2.5">
          <button type="button" onClick={onToggle} className="flex items-start gap-1.5 text-left">
            {isOpen ? <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /> : <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />}
            <span>
              <span className="block font-semibold text-slate-900">{c.name}</span>
              <span className="block text-[11.5px] text-slate-500">
                <span className={cn("mr-1 inline-flex items-center gap-0.5 rounded px-1 py-px text-[10px] font-semibold uppercase tracking-wide", c.channel === "sms" ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-500")}>{c.channel === "sms" ? <MessageSquare className="h-2.5 w-2.5" /> : <Mail className="h-2.5 w-2.5" />} {c.channel === "sms" ? "Text" : "Email"}</span>
                {c.channel === "sms" ? "" : templateName}{c.created_by ? ` · by ${c.created_by.split("@")[0]}` : ""}
              </span>
            </span>
          </button>
        </td>
        <td className="px-3 py-2.5"><span className={cn("inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold", meta.chip)}>{meta.label}</span></td>
        <td className="px-3 py-2.5 text-[12.5px] text-slate-700">
          <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-slate-400" /> {c.audience_label ?? "—"}</span>
          <span className="block text-[11px] text-slate-400">{total.toLocaleString()} recipient{total === 1 ? "" : "s"}{c.excluded_count ? ` · ${c.excluded_count} excluded (${c.channel === "sms" ? "no opt-in on record / no cell" : "unsubscribed / no address"})` : ""}</span>
        </td>
        <td className="px-3 py-2.5 text-[12.5px] text-slate-700">
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-slate-400" /> {c.status === "sent" ? `finished ${when(c.finished_at)}` : c.status === "cancelled" ? `cancelled ${when(c.finished_at)}` : when(c.scheduled_for)}</span>
          {["scheduled", "sending", "held"].includes(c.status) && total > 0 && <span className="block text-[11px] text-slate-400">{estimateDuration(total - done, c.throttle_per_run)} once running</span>}
        </td>
        <td className="px-3 py-2.5 text-right tabular-nums">
          <span className="text-[12.5px] font-semibold text-slate-800">{c.sent_count.toLocaleString()} sent</span>
          <span className="block text-[11px] text-slate-400">{total ? `${Math.round((done / total) * 100)}%` : "—"}{c.suppressed_count ? ` · ${c.suppressed_count} suppressed` : ""}{c.failed_count ? ` · ${c.failed_count} failed` : ""}</span>
          {total > 0 && (
            <span className="mt-1 block h-1.5 w-28 overflow-hidden rounded-full bg-slate-100">
              <span className="block h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, Math.round((done / total) * 100))}%` }} />
            </span>
          )}
        </td>
        <td className="px-3 py-2.5 text-right">
          <span className="inline-flex items-center gap-1">
            {onRelease && (
              <button type="button" disabled={busy} onClick={onRelease} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11.5px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><PlayCircle className="h-3.5 w-3.5" /> Release</button>
            )}
            {onCancel && (
              <button type="button" disabled={busy} onClick={onCancel} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-[11.5px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"><XCircle className="h-3.5 w-3.5" /> Cancel</button>
            )}
          </span>
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-slate-50/50">
          <td colSpan={6} className="px-4 py-3">
            {c.channel === "sms" && c.sms_body && (
              <div className="mb-3 flex items-start gap-2 text-[12px] text-slate-600">
                <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-500" />
                <div className="max-w-xl whitespace-pre-wrap rounded-2xl rounded-tl-sm bg-white px-3 py-2 ring-1 ring-slate-100">{c.sms_body}<span className="text-slate-400">{/\bSTOP\b/i.test(c.sms_body) ? "" : "\nReply STOP to opt out"}</span></div>
              </div>
            )}
            {c.sent_count > 0 && <ResultsStrip r={results.data ?? null} loading={results.isLoading} sms={c.channel === "sms"} />}
            {recipients.isLoading ? (
              <p className="text-xs text-slate-400">Loading recipients…</p>
            ) : (
              <div className="grid gap-1 text-[12px] sm:grid-cols-2 lg:grid-cols-3">
                {(recipients.data ?? []).slice(0, 300).map((r) => (
                  <div key={r.id} className="flex items-center gap-2 rounded-md bg-white px-2 py-1 ring-1 ring-slate-100">
                    {r.status === "sent" ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" /> : r.status === "failed" || r.status === "suppressed" ? <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" /> : <Clock className="h-3.5 w-3.5 shrink-0 text-slate-300" />}
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-medium text-slate-800">{r.customer_name ? displayCustomerName(null, r.customer_name) : (r.custno ?? "—")}</span>
                      <span className="text-slate-400"> · {r.to_address}</span>
                    </span>
                    <span className={cn("shrink-0 text-[10.5px] font-semibold", r.status === "sent" ? "text-emerald-700" : r.status === "failed" ? "text-rose-700" : r.status === "suppressed" ? "text-amber-700" : "text-slate-400")} title={r.status_detail ?? undefined}>
                      {r.status}
                    </span>
                  </div>
                ))}
                {(recipients.data?.length ?? 0) > 300 && <p className="text-[11px] text-slate-400">…and {(recipients.data!.length - 300).toLocaleString()} more</p>}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** MKT M4: the numbers that matter, in one row. Opens undercount (Apple Mail
 *  and image blocking); booked-within-30-days is the honest one. */
function ResultsStrip({ r, loading, sms }: { r: CampaignResults | null; loading: boolean; sms?: boolean }) {
  if (loading || !r) return <p className="mb-3 text-xs text-slate-400">{loading ? "Adding up the results…" : ""}</p>;
  const pct = (n: number) => (r.sent ? `${Math.round((n / r.sent) * 100)}%` : "—");
  const money = (n: number) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const Stat = ({ label, value, sub, tone, info }: { label: string; value: string; sub?: string; tone?: string; info?: React.ReactNode }) => (
    <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-slate-100">
      <div className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
        {info && <InfoTip className="-my-1 normal-case" label={`About ${label}`}>{info}</InfoTip>}
      </div>
      <div className={cn("text-base font-bold tabular-nums", tone ?? "text-slate-800")}>{value}</div>
      {sub && <div className="text-[10.5px] text-slate-400">{sub}</div>}
    </div>
  );
  return (
    <div className="mb-3">
      <div className={cn("grid grid-cols-2 gap-2 sm:grid-cols-3", sms ? "lg:grid-cols-4" : "lg:grid-cols-6")}>
        <Stat label="Sent" value={r.sent.toLocaleString()} sub={r.first_sent_at ? `from ${when(r.first_sent_at)}` : undefined} />
        {!sms && <Stat label="Opened" value={r.opened.toLocaleString()} sub={`${pct(r.opened)} · ${r.opens.toLocaleString()} opens`} info="Opens undercount, because Apple Mail and image blocking hide them." />}
        {!sms && <Stat label="Clicked" value={r.clicked.toLocaleString()} sub={`${pct(r.clicked)} · ${r.clicks.toLocaleString()} clicks`} />}
        <Stat label={sms ? "Opted out" : "Unsubscribed"} value={r.unsubscribed.toLocaleString()} sub={pct(r.unsubscribed)} tone={r.unsubscribed ? "text-amber-700" : undefined} />
        <Stat label="Booked in 30 days" value={r.booked_30d.toLocaleString()} sub={`${r.bookings_30d.toLocaleString()} call${r.bookings_30d === 1 ? "" : "s"}${r.window_open ? " · still counting" : ""}`} tone="text-emerald-700"
          info={`${sms ? "Texts don't report opens or clicks. " : ""}A call booked or an invoice paid within 30 days counts for the most recent campaign that customer received.`} />
        <Stat label="Paid in 30 days" value={money(r.revenue_30d)} sub={`${r.paid_30d.toLocaleString()} customer${r.paid_30d === 1 ? "" : "s"}`} tone="text-emerald-700" />
      </div>
      {r.top_links.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
          <span className="font-semibold text-slate-600">Top links:</span>
          {r.top_links.map((l) => <span key={l.url} className="truncate" title={l.url}>{l.url.replace(/^https?:\/\//, "").slice(0, 48)} · {l.n}</span>)}
        </div>
      )}
    </div>
  );
}

/** The "schedule it" form shared by Marketing and the All Customers report. */
export function CampaignLaunchForm({ defaultName, templateKey, templateName, audience, audienceLabel, recipients, excludedNote, onLaunched }: {
  defaultName: string;
  templateKey: string;
  templateName: string;
  audience: Record<string, unknown>;
  audienceLabel: string;
  recipients: { custno: string | null; to: string; name?: string | null; locno?: string | null }[];
  excludedNote?: string | null;
  onLaunched?: (c: Campaign) => void;
}) {
  const live = useCommsLive().data ?? false;
  const launch = useLaunchCampaign();
  const canSend = useCan("marketing.send");
  const [name, setName] = useState(defaultName);
  const [mode, setMode] = useState<"now" | "later">("now");
  const [at, setAt] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  });
  const count = recipients.length;
  return (
    <div className="rounded-xl border border-hairline bg-slate-50/50 p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Send className="h-4 w-4 text-brand-600" /> Send as a campaign
        <InfoTip label="Who is left out">Unsubscribed and do-not-email addresses are dropped when the campaign is scheduled, and checked again at send time.</InfoTip>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]">
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-slate-500">Campaign name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-800 focus:border-brand-500 focus:outline-none" />
        </label>
        <div>
          <span className="mb-1 block text-[11px] font-medium text-slate-500">When</span>
          <div className="flex items-center gap-1 rounded-full bg-white p-0.5 ring-1 ring-hairline">
            {(["now", "later"] as const).map((m) => (
              <button key={m} type="button" onClick={() => setMode(m)} className={cn("rounded-full px-3 py-1 text-xs font-semibold", mode === m ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-50")}>{m === "now" ? "Start now" : "Pick a time"}</button>
            ))}
          </div>
        </div>
      </div>
      {mode === "later" && (
        <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} className="mt-2 h-9 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-800 focus:border-brand-500 focus:outline-none" />
      )}
      <div className="mt-3 rounded-lg border border-hairline bg-white px-3 py-2.5 text-[12.5px] text-slate-700">
        <div><strong className="text-brand-700">{count.toLocaleString()}</strong> recipient{count === 1 ? "" : "s"} · {audienceLabel} · template <strong>{templateName}</strong></div>
        <div className="mt-0.5 text-[11.5px] text-slate-500">
          Sends {estimateDuration(count)} at 100 every 10 minutes.{excludedNote ? ` ${excludedNote}` : ""}
        </div>
        {!live && <div className="mt-1.5 flex items-center gap-1 text-[11.5px] font-medium text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> Comms are in testing: this campaign will be created and <b>held</b>. Nothing goes out until you press Release after go-live.</div>}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button type="button" disabled={launch.isPending || !name.trim() || count === 0 || !templateKey || !canSend}
          title={canSend ? undefined : "Sending campaigns needs the marketing.send permission — ask the owner under Settings → People & Access"}
          onClick={() => launch.mutate({
            name, templateKey, audience, audienceLabel, recipients,
            scheduledFor: mode === "later" ? new Date(at).toISOString() : new Date().toISOString(),
          }, { onSuccess: (c) => onLaunched?.(c) })}
          className={cn("inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50", live ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700")}>
          <Send className="h-4 w-4" /> {live ? (mode === "now" ? `Start campaign for ${count.toLocaleString()}` : `Schedule for ${count.toLocaleString()}`) : `Create held campaign for ${count.toLocaleString()}`}
        </button>
        {!canSend && <span className="text-xs text-slate-500">You can build and preview; sending needs the marketing.send permission.</span>}
        {launch.isPending && <span className="text-xs text-slate-500">Creating the campaign and queueing recipients…</span>}
        {launch.isError && <span className="text-xs text-red-700">{(launch.error as Error)?.message}</span>}
        {launch.isSuccess && <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> {launch.data.status === "held" ? "Campaign created and held" : "Campaign scheduled"} — {launch.data.recipient_count.toLocaleString()} queued{launch.data.excluded_count ? `, ${launch.data.excluded_count} excluded` : ""}. It's in the Campaigns list.</span>}
      </div>
    </div>
  );
}
